/**
 * The buttons a counter uses to put something on the till roll.
 *
 * Kept together so the bill printed from POS, the reprint from the register and
 * the kitchen slip all come off the same roll, the same width, laid out the
 * same way.
 */
import { useState } from 'react';
import { paperWidths, renderKot, renderReceipt, type PaperWidth, type ReceiptData } from '@kortex/shared';
import { paperWidth, printThermal, setPaperWidth } from '../lib/thermal';
import { useToast } from './ui';

/** The roll loaded in this counter's printer. Asked once, remembered after. */
export function PaperWidthPicker() {
  const [width, setWidth] = useState<PaperWidth>(paperWidth);
  return (
    <label className="row gap-6 small muted" style={{ marginRight: 'auto' }}>
      Roll
      <select
        className="input"
        style={{ width: 88, height: 32 }}
        value={width}
        onChange={(e) => {
          const next = Number(e.target.value) as PaperWidth;
          setPaperWidth(next);
          setWidth(next);
        }}
      >
        {paperWidths().map((option: PaperWidth) => (
          <option key={option} value={option}>
            {option}mm
          </option>
        ))}
      </select>
    </label>
  );
}

export function ThermalPrintButton({
  data,
  kind = 'receipt',
  label,
  className = 'btn',
}: {
  data: ReceiptData;
  kind?: 'receipt' | 'kot';
  label?: string;
  className?: string;
}) {
  const { fail } = useToast();
  return (
    <button
      className={className}
      title={`Prints on the ${paperWidth()}mm thermal printer set as this computer's printer`}
      onClick={() => {
        const width = paperWidth();
        const text = kind === 'kot' ? renderKot(data, width) : renderReceipt(data, width);
        // On the Android kiosk this goes to the native ESC/POS printer; in a
        // browser it opens the print dialog. Either way failures land in the
        // same toast.
        void printThermal(text, width, kind).catch(fail);
      }}
    >
      {label ?? (kind === 'kot' ? 'Print KOT' : 'Print receipt')}
    </button>
  );
}
